// 채팅 세션(대화 목록) 스토어 — 바닐라 app.js 의 loadSessions/persistSessions/
// serverListConversations/serverSaveConversation(재시도)/refreshConversationsFromServer(병합)/
// deleteSession/userScopeKey/첫인증 리셋 가드를 zustand 로 이식 (Phase 3-A).
//
// ⚠ 호환 계약 (바닐라 ↔ React 양방향):
//  - localStorage 키: `va_sessions::<userKey>` (userKey = uid||username||email 소문자, 없으면 _anon)
//  - 세션 스냅샷 형식은 바닐라 snapshot() 과 동일 —
//    {id,title,pinned,group,titleLocked,sub,ts,tsMs,messagesHtml,history,
//     traceSteps,traceGroups,targets,activeTargetIdx,userPickedTarget,traceTitle,traceKv}
//    messagesHtml 은 바닐라 openSession 이 그대로 innerHTML 하므로, React 도
//    바닐라와 같은 클래스 구조로 렌더한 DOM 의 innerHTML 을 채운다 (ChatPage 담당).
//  - 진실 소스는 서버 DB(/agent/conversations), localStorage 는 즉시 렌더용 캐시.
import { create } from "zustand";
import { authHeaders } from "../lib/api";
import type { VaTarget } from "../lib/chatText";
import { useAuth, type User } from "./auth";
import { userScopeKey } from "./dcf";

export interface HistMsg {
  role: "user" | "assistant";
  content: string;
}

export interface TraceRow {
  t: string;
  status: "run" | "ok" | "fail" | "warn";
}

export interface TraceGroup {
  tool: string;
  symbol: string | null;
  rows: TraceRow[];
  done: boolean;
  ok: boolean;
  open?: boolean;
}

/** 바닐라 snapshot() 형식 — 알 수 없는(구버전·향후) 필드는 보존해 되돌려 보낸다 */
export interface VaSession {
  id: string;
  title: string;
  pinned?: boolean;
  group?: string;
  titleLocked?: boolean;
  sub?: string;
  ts?: string;
  tsMs?: number;
  messagesHtml?: string;
  history?: HistMsg[];
  traceSteps?: Array<{ tool: string; ok: boolean }>;
  traceGroups?: TraceGroup[];
  targets?: VaTarget[];
  activeTargetIdx?: number;
  userPickedTarget?: boolean;
  traceTitle?: string;
  traceKv?: string;
  [k: string]: unknown;
}

const SESSIONS_BASE = "va_sessions";

function sessionsKey(userKey: string | null): string {
  return `${SESSIONS_BASE}::${userKey || "_anon"}`;
}

export function loadLocalSessions(userKey: string | null): VaSession[] {
  try {
    const arr = JSON.parse(
      localStorage.getItem(sessionsKey(userKey)) || "[]",
    ) as VaSession[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function persistLocalSessions(userKey: string | null, sessions: VaSession[]) {
  try {
    localStorage.setItem(sessionsKey(userKey), JSON.stringify(sessions.slice(0, 60)));
  } catch {
    /* quota 초과 등 — 캐시 저장 실패는 무시(서버가 진실 소스) */
  }
}

// ── 서버 DB 연동 — 바닐라와 동일 의미(실패 시 null, 절대 throw 로 흐름 끊지 않음) ──
export async function serverListConversations(): Promise<VaSession[] | null> {
  try {
    const r = await fetch("/agent/conversations", { headers: authHeaders() });
    if (!r.ok) return null;
    const d = (await r.json()) as { ok?: boolean; items?: VaSession[] };
    return d && d.ok ? d.items || [] : null;
  } catch {
    return null;
  }
}

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

/** 저장 재시도 포함 — 로그인 직후 토큰 검증 전파 지연(401 등)으로 첫 저장이
 *  실패해도 1.5s/3s 후 재시도해 유실을 막는다 (바닐라 최신 로직 그대로). */
export async function serverSaveConversation(
  conv: VaSession,
  attempt = 0,
): Promise<void> {
  try {
    const r = await fetch("/agent/conversations", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ conversation: conv }),
    });
    if (r.ok) return;
    if (attempt >= 2) return;
    await sleep(1500 * (attempt + 1));
    return serverSaveConversation(conv, attempt + 1);
  } catch {
    if (attempt >= 2) return;
    await sleep(1500 * (attempt + 1));
    return serverSaveConversation(conv, attempt + 1);
  }
}

export async function serverDeleteConversation(id: string): Promise<void> {
  try {
    await fetch(`/agent/conversations/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
  } catch {
    /* best-effort */
  }
}

// ── 그룹 (Phase 3-B) — 바닐라 _storedGroups/_collapsedGroups 이식 ──────────
// 사용자가 만든 그룹 목록(빈 그룹 포함)은 계정별 localStorage. 대화의 소속(group
// 필드)은 세션 스냅샷에 실려 서버 동기화된다. 접힘 상태는 표시 설정이라 로컬만.
const groupListKey = (userKey: string | null) => `va_groups::${userKey || "_anon"}`;
const collapsedKey = (userKey: string | null) => `va_groups_collapsed::${userKey || "_anon"}`;

export function storedGroups(userKey: string | null): string[] {
  try {
    const arr = JSON.parse(localStorage.getItem(groupListKey(userKey)) || "[]");
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}
function saveStoredGroups(userKey: string | null, list: string[]) {
  try {
    localStorage.setItem(groupListKey(userKey), JSON.stringify([...new Set(list)]));
  } catch {
    /* noop */
  }
}
export function collapsedGroups(userKey: string | null): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(collapsedKey(userKey)) || "[]"));
  } catch {
    return new Set();
  }
}
export function persistCollapsedGroups(userKey: string | null, set: Set<string>) {
  try {
    localStorage.setItem(collapsedKey(userKey), JSON.stringify([...set]));
  } catch {
    /* noop */
  }
}
/** 그룹 이름 전체 — 저장 목록 + 세션에서 파생(다른 기기에서 만든 그룹 포함) */
export function groupNames(userKey: string | null, sessions: VaSession[]): string[] {
  return [
    ...new Set([...storedGroups(userKey), ...sessions.map((s) => s.group).filter(Boolean) as string[]]),
  ].sort();
}

/** 고정된 대화를 위로. 그 외에는 최근 순서(서버·캐시가 준 순서)를 유지한다. */
export function sortedSessions(sessions: VaSession[]): VaSession[] {
  return sessions
    .map((s, i) => [s, i] as const)
    .sort(
      (a, b) =>
        (b[0].pinned ? 1 : 0) - (a[0].pinned ? 1 : 0) || a[1] - b[1],
    )
    .map((x) => x[0]);
}

interface ChatState {
  userKey: string | null;
  sessions: VaSession[];
  /** 응답 생성 중 — 사이드바 내비게이션·새 채팅을 잠근다 (바닐라 busy) */
  busy: boolean;
  /** 지금 응답을 작성 중인 대화 id 들 — 대화 전환을 열면 동시에 여럿일 수 있다 */
  writingIds: string[];
  addWriting: (id: string) => void;
  removeWriting: (id: string) => void;
  /** 내가 안 보는 사이 답변이 끝난 대화 — 사이드바에 파란 불릿으로 알린다.
   *  열어서 확인하면(openSession) 지워진다 — 늘 파랗다면 신호가 아니다 */
  doneIds: string[];
  markDone: (id: string) => void;
  clearDone: (id: string) => void;
  /** token 스트리밍 수신 중 */
  streaming: boolean;
  /** 실제 계정 전환 시에만 증가 — ChatPage 가 감지해 현재 대화를 리셋.
   *  페이지 로드 후 첫 인증(null→계정)은 올리지 않는다: 로그인 완료 직전에 시작한
   *  대화와 /c/<id> 딥링크가 지워지던 유실 버그 가드 (바닐라 setAuthed 동일). */
  resetEpoch: number;

  /** 로그인 사용자 기준 초기화 — 캐시 즉시 렌더 후 서버 기록으로 병합 교체 */
  init: (user: User | null) => void;
  /** 서버 목록 + 최근 2분 내 로컬 신규분 병합·재푸시 (바닐라 refreshConversationsFromServer) */
  refreshFromServer: () => Promise<void>;
  /** 탭 재노출 시 재동기화 — 30초 내 재조회 방지 (바닐라 resyncConversationsIfVisible) */
  maybeResync: () => void;
  /** 스냅샷 upsert — 기존 세션의 pinned/group/titleLocked 는 유지(바닐라 snapshot 의 prev 병합) */
  saveSession: (snap: VaSession) => void;
  deleteSession: (id: string) => void;
  setBusy: (b: boolean) => void;
  setStreaming: (b: boolean) => void;
  // ── 대화 메뉴·그룹 (3-B) — 각 변경은 캐시 + 서버 저장까지 ──
  togglePin: (id: string) => void;
  /** 이름 변경 — titleLocked 로 자동 제목이 다시 덮지 않게 잠근다 */
  renameSession: (id: string, title: string) => void;
  /** 그룹 이동 (undefined = 그룹에서 빼기) */
  setSessionGroup: (id: string, group: string | undefined) => void;
  addGroup: (name: string) => void;
  renameGroup: (from: string, to: string) => void;
  /** 그룹 삭제 — 소속 대화는 '채팅'(그룹 없음)으로 이동 */
  deleteGroup: (name: string) => void;
}

let lastSyncMs = 0;

export const useChat = create<ChatState>((set, get) => ({
  userKey: null,
  sessions: [],
  busy: false,
  writingIds: [],
  addWriting: (id) =>
    set((s) => (s.writingIds.includes(id) ? s : { writingIds: [...s.writingIds, id] })),
  removeWriting: (id) =>
    set((s) => ({ writingIds: s.writingIds.filter((x) => x !== id) })),
  doneIds: [],
  markDone: (id) =>
    set((s) => (s.doneIds.includes(id) ? s : { doneIds: [...s.doneIds, id] })),
  clearDone: (id) =>
    set((s) => (s.doneIds.includes(id) ? { doneIds: s.doneIds.filter((x) => x !== id) } : s)),
  streaming: false,
  resetEpoch: 0,

  init: (user) => {
    const newKey = userScopeKey(user);
    const prevKey = get().userKey;
    if (newKey === prevKey && newKey !== null) return;
    if (newKey) {
      try {
        localStorage.setItem("va_active_user", newKey);
      } catch {
        /* noop */
      }
    }
    // 화면 초기화는 "실제 계정 전환"(이전 계정 잔상 제거)일 때만 (첫인증 리셋 가드)
    const switched = prevKey !== null && newKey !== prevKey;
    set({
      userKey: newKey,
      sessions: loadLocalSessions(newKey), // 캐시로 즉시 렌더
      ...(switched ? { resetEpoch: get().resetEpoch + 1 } : {}),
    });
    void get().refreshFromServer(); // 서버 DB 기록으로 교체(계정 격리 최종 보증)
  },

  refreshFromServer: async () => {
    lastSyncMs = Date.now();
    const scopeAtStart = get().userKey;
    const fetchStart = Date.now();
    const items = await serverListConversations();
    if (items === null) return; // 서버 오류 → 캐시 유지
    if (scopeAtStart !== get().userKey) return; // 그 사이 계정 전환됨 → 무시
    // 병합: 응답이 도착하기 전(스테일 스냅샷) 로컬에서 만들거나 갱신한 대화는
    // 지우지 않고 앞에 유지 + 서버에 재푸시(자기치유). 그 외에는 서버가 진실.
    const have = new Set(items.map((x) => x.id));
    const fresh = get().sessions.filter(
      (x) => !have.has(x.id) && Number(x.tsMs) > fetchStart - 120000,
    );
    const merged = fresh.concat(items);
    fresh.forEach((x) => void serverSaveConversation(x));
    set({ sessions: merged });
    persistLocalSessions(get().userKey, merged);
  },

  maybeResync: () => {
    if (typeof document !== "undefined" && document.hidden) return;
    if (!useAuth.getState().user) return;
    if (Date.now() - lastSyncMs < 30000) return; // 30초 내 재조회 방지
    void get().refreshFromServer();
  },

  saveSession: (snap) => {
    const sessions = get().sessions.slice();
    const i = sessions.findIndex((s) => s.id === snap.id);
    const prev = i >= 0 ? sessions[i] : undefined;
    // 저장할 때마다 고정·그룹·이름 잠금이 풀리지 않게 유지 (바닐라 snapshot 의 prev 병합)
    const merged: VaSession = {
      ...snap,
      pinned: !!(prev && prev.pinned),
      group: (prev && prev.group) || snap.group || undefined,
      titleLocked: !!((prev && prev.titleLocked) || snap.titleLocked),
    };
    if (i >= 0) sessions[i] = merged;
    else sessions.unshift(merged);
    set({ sessions });
    persistLocalSessions(get().userKey, sessions); // 캐시
    void serverSaveConversation(merged); // 서버 DB(계정별, 비동기·재시도)
  },

  deleteSession: (id) => {
    const sessions = get().sessions.filter((x) => x.id !== id); // 낙관적 제거
    set({ sessions });
    persistLocalSessions(get().userKey, sessions);
    void serverDeleteConversation(id); // 서버 DB 삭제(비동기)
  },

  setBusy: (b) => set({ busy: b }),
  setStreaming: (b) => set({ streaming: b }),

  togglePin: (id) => {
    const sessions = get().sessions.slice();
    const s = sessions.find((x) => x.id === id);
    if (!s) return;
    s.pinned = !s.pinned;
    set({ sessions });
    persistLocalSessions(get().userKey, sessions);
    void serverSaveConversation(s);
  },

  renameSession: (id, title) => {
    const next = (title || "").trim().slice(0, 60);
    const sessions = get().sessions.slice();
    const s = sessions.find((x) => x.id === id);
    if (!s || !next || next === s.title) return;
    s.title = next;
    s.titleLocked = true; // 자동 제목 생성이 다시 덮어쓰지 않게
    set({ sessions });
    persistLocalSessions(get().userKey, sessions);
    void serverSaveConversation(s);
  },

  setSessionGroup: (id, group) => {
    const sessions = get().sessions.slice();
    const s = sessions.find((x) => x.id === id);
    if (!s) return;
    s.group = group === s.group ? undefined : group;
    set({ sessions });
    persistLocalSessions(get().userKey, sessions);
    void serverSaveConversation(s);
  },

  addGroup: (name) => {
    const g = (name || "").trim().slice(0, 30);
    if (!g) return;
    saveStoredGroups(get().userKey, [...storedGroups(get().userKey), g]);
    set({ sessions: get().sessions.slice() }); // 리렌더 트리거
  },

  renameGroup: (from, to) => {
    const next = (to || "").trim().slice(0, 30);
    if (!next || next === from) return;
    const key = get().userKey;
    saveStoredGroups(key, storedGroups(key).map((g) => (g === from ? next : g)));
    const sessions = get().sessions.slice();
    sessions.forEach((s) => {
      if (s.group === from) {
        s.group = next;
        void serverSaveConversation(s);
      }
    });
    set({ sessions });
    persistLocalSessions(key, sessions);
  },

  deleteGroup: (name) => {
    const key = get().userKey;
    saveStoredGroups(key, storedGroups(key).filter((g) => g !== name));
    const sessions = get().sessions.slice();
    sessions.forEach((s) => {
      if (s.group === name) {
        s.group = undefined;
        void serverSaveConversation(s);
      }
    });
    set({ sessions });
    persistLocalSessions(key, sessions);
  },
}));
